import React, { useEffect, useState, useRef } from "react";
import {
  PageHeader,
  SectionCard,
  StatCard,
  DashboardSkeleton,
  EmptyState,
  TableShell,
} from "../../ui";
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Typography,
  DialogActions,
  Table,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import PermIdentityOutlinedIcon from "@mui/icons-material/PermIdentityOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import { Payment, Payments, Receipt, Person4, Person } from "@mui/icons-material";
import {
  isDynamicStaffRole,
  hasScreen,
  hasPermission,
  canViewDashboardOverview,
  canReadScreen,
  hasCompanyFeature,
} from "../../utils/roleAccess";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import BarChartIcon from "@mui/icons-material/BarChart";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import { motion, useReducedMotion } from "motion/react";
import Popup from "../../base-components/Popup";
import { gx, fontFamily } from "../../theme/tokens";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const BASE_URL = process.env.REACT_APP_BASE_URL;
const FEATURED_KEYS = [
  "activeMembers",
  "membersCount",
  "feeCollectedPerDay",
  "expectedIncomePerMonth",
];

const STAT_GROUPS = [
  {
    title: "People",
    keys: ["newMembers", "restoredMembers", "inactiveMembers", "trainersCount"],
  },
  {
    title: "Club",
    keys: ["packagesCount", "locationsCount", "assetsCount", "facilitiesCount"],
  },
  {
    title: "Today",
    keys: ["invoicesPerDay", "memberAttendance", "staffAttendance"],
  },
];

const SHORTCUTS = [
  { label: "Members", to: "/members", screen: "members", icon: <GroupsOutlinedIcon fontSize="small" /> },
  { label: "Attendance", to: "/memberattendance", screen: "staff_attendance", icon: <Person4 fontSize="small" /> },
  { label: "POS", to: "/pos", screen: "pos", icon: <PointOfSaleIcon fontSize="small" /> },
  { label: "Invoices", to: "/invoice-report", screen: "reporting", icon: <Receipt fontSize="small" /> },
];

const WELCOME_MODULES = [
  { label: "Subscriptions", to: "/ledgers", screen: "subscriptions" },
  { label: "Members", to: "/members", screen: "members" },
  { label: "Company ledgers", to: "/company-ledgers", screen: "company_ledgers" },
  { label: "Expense", to: "/expense", screen: "expense" },
  { label: "Point of sale", to: "/pos", screen: "pos" },
  { label: "Reporting", to: "/income-breakdown-report", screen: "reporting" },
  { label: "Staff attendance", to: "/staffattendance", screen: "staff_attendance" },
];

const chartFont = {
  family: fontFamily,
  size: 12,
};

const headingMap = {
  companiesCount: {
    label: "Companies Registered",
    icon: <AccountBalanceOutlinedIcon fontSize="small" />,
    link: "/companies",
  },
  membersCount: {
    label: "Total Members",
    icon: <PermIdentityOutlinedIcon fontSize="small" />,
    link: "/members",
  },
  activeMembers: {
    label: "Active Members",
    icon: <CheckCircleOutlineOutlinedIcon fontSize="small" />,
    link: "/members",
  },
  inactiveMembers: {
    label: "Inactive Members",
    icon: <PersonOffOutlinedIcon fontSize="small" />,
    link: "/members",
  },
  newMembers: {
    label: "New Members (last 30 days)",
    icon: <PersonAddOutlinedIcon fontSize="small" />,
    link: "/members",
  },
  restoredMembers: {
    label: "Reactive Members",
    icon: <RestoreIcon fontSize="small" />,
    link: "/members",
  },
  packagesCount: {
    label: "Packages",
    icon: <CardGiftcardIcon fontSize="small" />,
    link: "/packages",
  },
  trainersCount: {
    label: "Number of Trainers",
    icon: <SupervisorAccountIcon fontSize="small" />,
    link: "/trainers",
  },
  locationsCount: {
    label: "Locations",
    icon: <MyLocationIcon fontSize="small" />,
    link: "/locations",
  },
  assetsCount: {
    label: "Assets",
    icon: <FitnessCenterIcon fontSize="small" />,
    link: "/assets",
  },
  facilitiesCount: {
    label: "Facilities",
    icon: <AccountBalanceOutlinedIcon fontSize="small" />,
    link: "/facilities",
  },
  expectedIncomePerMonth: {
    label: "This Month Expected Income",
    icon: <Payments fontSize="small" />,
    link: "/expected-income",
    money: true,
  },
  memberAttendance: {
    label: "Member Attendance",
    icon: <Person4 fontSize="small" />,
    link: "/memberattendance",
  },
  feeCollectedPerDay: {
    label: "Today's Fee Collection",
    icon: <Payment fontSize="small" />,
    link: "/fee-collection-report",
    money: true,
  },
  invoicesPerDay: {
    label: "Invoices Created Today",
    icon: <Receipt fontSize="small" />,
    link: "/invoice-report",
  },
  staffAttendance: {
    label: "Staff Attendance",
    icon: <Person fontSize="small" />,
    link: "/staffattendance",
  },
};

const roleCopy = {
  SuperAdmin: "Platform admin",
  Admin: "Admin",
  ClubAdmin: "Club admin",
  Trainer: "Trainer",
  User: "Member",
};

function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return value ?? "0";
  return n.toLocaleString("en-US");
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return `${value ?? 0} Rs`;
  return `Rs ${n.toLocaleString("en-US")}`;
}

function formatStatValue(key, value) {
  const meta = headingMap[key];
  if (meta?.money) return formatMoney(value);
  return formatNumber(value);
}

function percentLabel(part, total) {
  if (!total) return "0%";
  return `${Math.round((part / total) * 100)}%`;
}

function overviewDate() {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
  }).format(new Date());
}

function buildRosterBars(stats) {
  return [
    { label: "Active", value: Number(stats.activeMembers), color: gx.navy },
    { label: "Inactive", value: Number(stats.inactiveMembers), color: gx.inkSoft },
    { label: "New", value: Number(stats.newMembers), color: gx.red },
  ].filter((row) => Number.isFinite(row.value));
}

function buildAttendanceSlices(stats) {
  if (stats.memberAttendance === undefined || stats.memberAttendance === null) {
    return null;
  }
  const checkedIn = Number(stats.memberAttendance);
  const total = Number(stats.membersCount);
  if (!Number.isFinite(checkedIn) || checkedIn < 0) return null;
  if (Number.isFinite(total) && total > 0) {
    return {
      checkedIn,
      notYet: Math.max(0, total - checkedIn),
    };
  }
  return { checkedIn, notYet: 0 };
}

function ReportTable({ title, rows }) {
  const list = Array.isArray(rows) ? rows : [];
  const qty = list.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
  const amount = list.reduce(
    (sum, item) => sum + (Number(item.totalEarnings) || 0),
    0
  );

  return (
    <section>
      <Typography
        variant="h3"
        className="print:font-semibold"
        sx={{
          fontWeight: 700,
          my: "1rem",
          fontSize: "20px",
          color: gx.ink,
        }}
      >
        {title}
      </Typography>
      {list.length === 0 ? (
        <p className="text-sm text-gx-ink-soft py-3">No rows for this report.</p>
      ) : (
        <TableShell>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row, index) => (
                <TableRow key={`${row.name}-${index}`}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{formatNumber(row.quantity)}</TableCell>
                  <TableCell>{formatMoney(row.totalEarnings)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}></TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{formatNumber(qty)}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{formatMoney(amount)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableShell>
      )}
    </section>
  );
}

function Dashboard() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [userName, setUserName] = useState("");
  const [role, setRole] = useState("");
  const [sessionUser, setSessionUser] = useState(null);
  const [company_id, setCompanyID] = useState("");
  const [location_id, setLocationID] = useState("");
  const [member_id, setMemberID] = useState("");
  const [trainer_id, setTrainerID] = useState("");
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [member, setMember] = useState({});
  const [buttonLoading, setButtonLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [popup, setPopup] = useState({ open: false, type: "error", message: "" });
  const printRef = useRef();

  useEffect(() => {
    const sessionStr = sessionStorage.getItem("user-info");
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      const nextRole = session.user.role;
      setRole(nextRole);
      setSessionUser(session.user);
      setUserName(session.user.name);
      setCompanyID(session.user.company_id || "");
      setLocationID(session.user.location_id || "");
      setMemberID(session.user.member_id || "");
      setTrainerID(session.user.trainer_id || "");

      const roleKey = String(nextRole || "").toLowerCase();
      if (roleKey === "user" || roleKey === "member") {
        navigate("/member/dashboard", { replace: true });
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const showDashboardOverview = canViewDashboardOverview(sessionUser, role);

  useEffect(() => {
    if (!role || !sessionUser) return;

    if (!showDashboardOverview) {
      setLoading(false);
      setStats({});
      setLoadError("");
      return;
    }

    setLoading(true);
    getDashboardStats();
    if (member_id) {
      getMemberByID();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, sessionUser, company_id, location_id, showDashboardOverview]);

  const bufferToBase64 = (buffer) => {
    const uint8Array = new Uint8Array(buffer);
    let binary = "";
    uint8Array.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return `data:image/jpeg;base64,${window.btoa(binary)}`;
  };

  async function getMemberByID() {
    if (!member_id) {
      console.error("Member ID is not available.");
      return;
    }
    await fetch(BASE_URL + `members/get-member/${member_id}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.member.photo?.type === "Buffer") {
          data.member.photo = bufferToBase64(data.member.photo.data);
        }
        setMember(data.member);
      })
      .catch((error) => console.log(error));
  }

  const showPopup = (message, type = "error") => {
    setPopup({ open: true, type, message });
    setTimeout(() => setPopup({ open: false, type, message: "" }), 4000);
  };

  const handleDailyReport = async () => {
    setButtonLoading(true);
    try {
      const payload = {
        locationId: location_id,
      };
      const response = await fetch(`${BASE_URL}voucher/getDailyReport`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const res = await response.json();
        setReportData(res.data);
        setModalOpen(true);
      } else {
        showPopup("Daily report could not be generated. Try again.");
      }
    } catch (error) {
      console.error("Error generating daily report:", error);
      showPopup("Daily report could not be generated. Try again.");
    } finally {
      setButtonLoading(false);
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const newWindow = window.open("", "_blank");
    newWindow.document.write(`
      <html>
        <head>
          <title>Daily Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
            th { background-color: #f2f2f2; }
            .total { font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Daily Report</h1>
          <hr style="border: 1px solid #ddd; margin-bottom: 25px; width: 50%;" />
          <div>${printContent.innerHTML}</div>
          <hr style="border: 1px solid #ddd; margin: 30px 0;" />
          <div class="total print:font-semibold print:text-[25px]" style="margin-top: 30px; text-align: right;">
            <p>Total Quantity: ${reportData?.totalQuantity}</p>
            <p>Total Earnings: Rs ${reportData?.totalEarnings}</p>
          </div>
        </body>
      </html>
    `);
    newWindow.document.close();
    newWindow.print();
    newWindow.close();
  };

  const getDashboardStats = async () => {
    let data = {};
    setLoadError("");

    switch (role) {
      case "SuperAdmin":
        break;
      case "Admin":
        if (!company_id) {
          console.error("Company ID is missing for Admin role.");
          setLoading(false);
          setLoadError("Company ID is missing.");
          return;
        }
        data = { role, company_id };
        break;
      case "ClubAdmin":
        if (!location_id) {
          console.error("Location ID is missing for ClubAdmin role.");
          setLoading(false);
          setLoadError("Location ID is missing.");
          return;
        }
        data = { role, location_id };
        break;
      case "User":
        if (!member_id) {
          console.error("Member ID is missing for Member role.");
          setLoading(false);
          return;
        }
        data = { role, member_id };
        break;
      case "Trainer":
        if (!trainer_id) {
          console.error("Trainer ID is missing for Trainer role.");
          setLoading(false);
          setLoadError("Trainer ID is missing.");
          return;
        }
        data = { role, trainer_id };
        break;
      default:
        if (isDynamicStaffRole(role, sessionUser)) {
          if (location_id) {
            data = { role, company_id, location_id };
          } else if (company_id) {
            data = { role, company_id };
          } else {
            setLoading(false);
            setLoadError("This account is missing company or location scope for overview stats.");
            return;
          }
        } else {
          console.error("Unhandled role:", role);
          setLoading(false);
          return;
        }
    }

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    };

    try {
      if (role === "SuperAdmin") {
        const session = JSON.parse(sessionStorage.getItem("user-info") || "{}");
        const actingUserId = session?.user?._id;
        const response = await fetch(
          `${BASE_URL}super-admin/stats?actingUserId=${actingUserId}`
        );
        if (!response.ok) {
          setLoadError("Platform overview could not be loaded.");
        } else {
          const responseData = await response.json();
          setStats(responseData.stats || {});
        }
        return;
      }

      const response = await fetch(
        `${BASE_URL}users/getDashboardStats`,
        requestOptions
      );
      if (!response.ok) {
        const responseData = await response.json();
        console.error("API error:", responseData);
        setLoadError("Club overview could not be loaded.");
      } else {
        const responseData = await response.json();
        setStats(responseData.stats || {});
      }
    } catch (error) {
      console.error("Network error:", error);
      setLoadError("Club overview could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = () => {
    navigate("/profilemember", { state: { id: member } });
  };

  const goToStat = (key) => {
    const meta = headingMap[key];
    if (!meta) return;
    if (meta.label === "This Month Expected Income") {
      navigate(`${meta.link}/${stats[key]}`);
      return;
    }
    navigate(meta.link);
  };

  const visibleKeys = Object.keys(stats).filter((key) => headingMap[key]);
  const isPlatform = role === "SuperAdmin";
  const featuredKeys = isPlatform
    ? ["companiesCount", "locationsCount", "membersCount"].filter((key) =>
        visibleKeys.includes(key)
      )
    : FEATURED_KEYS.filter((key) => visibleKeys.includes(key));
  const groupedKeys = isPlatform
    ? new Set()
    : new Set(STAT_GROUPS.flatMap((group) => group.keys));
  const leftoverKeys = visibleKeys.filter(
    (key) => !featuredKeys.includes(key) && !groupedKeys.has(key)
  );

  const rosterRows = buildRosterBars(stats);
  const attendance = buildAttendanceSlices(stats);
  const attendanceTotal = attendance
    ? attendance.checkedIn + attendance.notYet
    : 0;

  const rosterData =
    rosterRows.length > 0
      ? {
          labels: rosterRows.map((row) => row.label),
          datasets: [
            {
              data: rosterRows.map((row) => row.value),
              backgroundColor: rosterRows.map((row) => row.color),
              borderRadius: 8,
              borderSkipped: false,
              maxBarThickness: 42,
              label: "Members",
            },
          ],
        }
      : null;

  const attendanceData = attendance
    ? {
        labels: [
          `Checked in ${percentLabel(attendance.checkedIn, attendanceTotal || attendance.checkedIn)}`,
          `Not yet ${percentLabel(attendance.notYet, attendanceTotal)}`,
        ],
        datasets: [
          {
            data: [attendance.checkedIn, attendance.notYet],
            backgroundColor: [gx.navy, gx.orange],
            borderWidth: 0,
            hoverOffset: 4,
          },
        ],
      }
    : null;

  const featuredHint = (key) => {
    if (key === "activeMembers" && Number.isFinite(Number(stats.membersCount)) && Number(stats.membersCount) > 0) {
      return `${percentLabel(stats.activeMembers, stats.membersCount)} of roster`;
    }
    if (key === "membersCount" && stats.inactiveMembers !== undefined) {
      return `${formatNumber(stats.inactiveMembers)} inactive`;
    }
    if (key === "feeCollectedPerDay") return "Collected today";
    if (key === "expectedIncomePerMonth") return "This month";
    return undefined;
  };

  const stagger = (index) =>
    reduceMotion
      ? false
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: {
            duration: 0.45,
            delay: index * 0.06,
            ease: [0.16, 1, 0.3, 1],
          },
        };

  const retryLoad = () => {
    setLoading(true);
    getDashboardStats();
  };

  const welcomeModules = WELCOME_MODULES.filter((item) => {
    if (!sessionUser) return false;
    if (!hasCompanyFeature(sessionUser, item.screen)) return false;
    if (isDynamicStaffRole(role, sessionUser) && !hasScreen(sessionUser, item.screen)) {
      return false;
    }
    return canReadScreen(sessionUser, item.screen);
  });

  return (
    <div className="relative">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-8 left-1/2 h-56 w-[min(100%,920px)] -translate-x-1/2"
        style={{
          background:
            "radial-gradient(ellipse at 50% 0%, rgba(2, 42, 58, 0.07), transparent 70%)",
        }}
      />

      {popup.open ? (
        <Popup
          onClose={() => setPopup({ open: false, type: "error", message: "" })}
          alertType={popup.type}
          message={popup.message}
        />
      ) : null}

      <PageHeader
        title={
          <>
            Welcome, <span className="text-gx-red">{userName}</span>
          </>
        }
        subtitle={`${roleCopy[role] || "Staff"} · ${overviewDate()}`}
        actions={
          <div className="flex items-center gap-3">
            {showDashboardOverview && location_id && hasPermission(sessionUser, "reporting:read") && (
              <button
                className="gx-cta min-h-[44px]"
                onClick={handleDailyReport}
                disabled={buttonLoading}
              >
                <BarChartIcon className="mr-2" fontSize="small" />
                {buttonLoading ? (
                  <>
                    Generating
                    <CircularProgress
                      size={13}
                      className="ml-2"
                      color="inherit"
                    />
                  </>
                ) : (
                  <>
                    <span className="sm:hidden">Daily Report</span>
                    <span className="hidden sm:inline">Generate Daily Report</span>
                  </>
                )}
              </button>
            )}
            {role == "User" ? (
              <button
                className="gx-cta-secondary min-h-[44px]"
                onClick={() => handleNavigate(member_id)}
              >
                View profile
              </button>
            ) : (
              ""
            )}
          </div>
        }
      />

      {showDashboardOverview && role !== "SuperAdmin" && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-6">
        {SHORTCUTS.filter((item) => {
          if (!isDynamicStaffRole(role, sessionUser)) return true;
          if (!hasScreen(sessionUser, item.screen)) return false;
          return hasPermission(sessionUser, `${item.screen}:read`);
        }).map((item) => (
          <button
            key={item.to}
            type="button"
            onClick={() => navigate(item.to)}
            className="gx-cta-secondary min-h-[44px] w-full gap-2"
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>
      )}

      {!showDashboardOverview ? (
        <SectionCard glass className="mt-2">
          <div className="py-10 sm:py-14 px-4 text-center max-w-lg mx-auto">
            <p className="text-xs font-semibold uppercase tracking-widest text-gx-ink-soft mb-3">
              GymXS
            </p>
            <h2 className="text-2xl sm:text-3xl font-semibold text-gx-navy tracking-tight text-balance">
              Welcome to your workspace
            </h2>
            <p className="text-sm text-gx-ink-soft mt-3 leading-relaxed text-pretty">
              Your role does not include the club overview dashboard. Open a module below
              or use the menu to get started.
            </p>
            {welcomeModules.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-2 mt-8">
                {welcomeModules.map((item) => (
                  <button
                    key={item.to}
                    type="button"
                    className="gx-cta-secondary min-h-[44px] px-4"
                    onClick={() => navigate(item.to)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gx-ink-soft mt-6">
                Use the sidebar to navigate to your assigned screens.
              </p>
            )}
          </div>
        </SectionCard>
      ) : loading ? (
        <DashboardSkeleton />
      ) : loadError ? (
        <EmptyState
          title="Overview unavailable"
          description={loadError}
          action={
            <button type="button" className="gx-cta min-h-[44px]" onClick={retryLoad}>
              Try again
            </button>
          }
        />
      ) : visibleKeys.length === 0 ? (
        <EmptyState
          title="No club numbers yet"
          description="Stats will show here once members, billing or attendance start coming in."
        />
      ) : (
        <div className="space-y-8">
          {featuredKeys.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {featuredKeys.map((key, index) => {
                const meta = headingMap[key];
                const motionProps = stagger(index);
                const card = (
                  <StatCard
                    featured
                    label={meta.label}
                    value={formatStatValue(key, stats[key])}
                    icon={meta.icon}
                    hint={featuredHint(key)}
                    onClick={() => goToStat(key)}
                  />
                );
                if (!motionProps) return <div key={key}>{card}</div>;
                return (
                  <motion.div key={key} {...motionProps}>
                    {card}
                  </motion.div>
                );
              })}
            </div>
          )}

          {(rosterData || attendanceData) && role !== "SuperAdmin" && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              {rosterData ? (
                <div className={attendanceData ? "lg:col-span-7" : "lg:col-span-12"}>
                  <SectionCard
                    glass
                    title="Roster"
                    subtitle="Active, inactive and new"
                    icon={<GroupsOutlinedIcon fontSize="small" />}
                  >
                    <div className="h-[220px] w-full">
                      <Bar
                        data={rosterData}
                        options={{
                          plugins: {
                            legend: { display: false },
                            tooltip: {
                              callbacks: {
                                label: (ctx) => ` ${formatNumber(ctx.parsed.y)}`,
                              },
                            },
                          },
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            x: {
                              grid: { display: false },
                              ticks: { font: chartFont, color: gx.inkSoft },
                              border: { display: false },
                            },
                            y: {
                              grid: { color: gx.border },
                              ticks: {
                                font: chartFont,
                                color: gx.inkSoft,
                                callback: (value) => formatNumber(value),
                              },
                              border: { display: false },
                            },
                          },
                        }}
                      />
                    </div>
                  </SectionCard>
                </div>
              ) : null}
              {attendanceData ? (
                <div className={rosterData ? "lg:col-span-5" : "lg:col-span-12"}>
                  <SectionCard
                    glass
                    title="Attendance"
                    subtitle="Checked in today"
                    icon={<DonutLargeIcon fontSize="small" />}
                    action={
                      <button
                        type="button"
                        className="text-sm font-semibold text-gx-red min-h-[44px] px-2"
                        onClick={() => navigate("/memberattendance")}
                      >
                        Open
                      </button>
                    }
                  >
                    <div className="relative h-[220px] w-full">
                      <Doughnut
                        data={attendanceData}
                        options={{
                          cutout: "72%",
                          plugins: {
                            legend: {
                              position: "bottom",
                              labels: {
                                boxWidth: 12,
                                padding: 16,
                                font: chartFont,
                                color: gx.ink,
                                usePointStyle: true,
                              },
                            },
                            tooltip: {
                              callbacks: {
                                label: (ctx) => ` ${formatNumber(ctx.parsed)}`,
                              },
                            },
                          },
                          responsive: true,
                          maintainAspectRatio: false,
                        }}
                      />
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-10">
                        <p className="text-3xl font-bold text-gx-navy tracking-tight">
                          {formatNumber(attendance.checkedIn)}
                        </p>
                        <p className="text-xs text-gx-ink-soft">checked in</p>
                      </div>
                    </div>
                  </SectionCard>
                </div>
              ) : null}
            </div>
          )}

          {STAT_GROUPS.map((group) => {
            const keys = group.keys.filter((key) => visibleKeys.includes(key) && !featuredKeys.includes(key));
            if (keys.length === 0) return null;
            return (
              <section key={group.title}>
                <h2 className="text-base font-semibold text-gx-ink mb-3">
                  {group.title}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                  {keys.map((key) => {
                    const meta = headingMap[key];
                    return (
                      <StatCard
                        key={key}
                        label={meta.label}
                        value={formatStatValue(key, stats[key])}
                        icon={meta.icon}
                        onClick={() => goToStat(key)}
                      />
                    );
                  })}
                </div>
              </section>
            );
          })}

          {leftoverKeys.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {leftoverKeys.map((key) => {
                const meta = headingMap[key];
                return (
                  <StatCard
                    key={key}
                    label={meta.label}
                    value={formatStatValue(key, stats[key])}
                    icon={meta.icon}
                    onClick={() => goToStat(key)}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: gx.surface,
            color: gx.ink,
            borderRadius: "12px",
            border: `1px solid ${gx.border}`,
            boxShadow: "0 8px 24px rgba(2, 42, 58, 0.08)",
          },
        }}
      >
        <div ref={printRef} className="p-6">
          <style>
            {`
            @media print {
              .print\\:hidden {
                  display: none !important;
              }
              .print\\font-semibold {
                  font-weight: 600 !important;
                  font-size: 20px !important;
              }
              .print\\:text-[25px] {
                  font-size: 25px !important;
              }
              .print\\:fontweight-bold {
                  font-weight: bold !important;
              }
          `}
          </style>
          <DialogTitle
            variant="h5"
            className="px-0 pt-0 tracking-tight border-b border-gx-border print:hidden"
            sx={{ fontWeight: 700, color: gx.ink }}
          >
            Daily Report Summary
          </DialogTitle>

          <DialogContent className="px-0 pt-2 pb-2 space-y-8">
            <ReportTable title="Package Report" rows={reportData?.packageReport} />
            <ReportTable title="Trainer Report" rows={reportData?.trainerReport} />

            <div className="flex flex-wrap justify-end gap-x-10 gap-y-2 border-t border-gx-border pt-5 text-gx-ink text-[15px] print:hidden">
              <div>
                <span className="font-semibold">Total Qty:</span>{" "}
                {formatNumber(reportData?.totalQuantity)}
              </div>
              <div>
                <span className="font-semibold">Total Earnings:</span>{" "}
                {formatMoney(reportData?.totalEarnings)}
              </div>
            </div>
          </DialogContent>

          <DialogActions
            className="px-0 pb-0 pt-3 flex justify-center items-center gap-4 w-full print:hidden"
            sx={{ display: "flex", justifyContent: "center", mt: "1rem" }}
          >
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="gx-cta-secondary min-h-[44px]"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => handlePrint()}
              className="gx-cta min-h-[44px]"
            >
              Download Report
            </button>
          </DialogActions>
        </div>
      </Dialog>
    </div>
  );
}

export default Dashboard;
